import { useState, useEffect, useRef } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import {PlatformRouteGate} from '../lib/toolAccess.jsx'
import ErrorBoundary from './ErrorBoundary.jsx'
import Sidebar from './Sidebar.jsx'
import TopBar from './TopBar.jsx'
import Footer from './Footer.jsx'
import ActingAsBanner from './ActingAsBanner.jsx'
import styles from './AppShell.module.css'

/**
 * The persistent application chrome: left sidebar, top bar, routed content,
 * and footer. Content routes render inside a path-keyed error boundary. Query edits must
 * preserve the mounted page (search focus, drafts and selected job state).
 */
export default function AppShell() {
  const [navOpen, setNavOpen] = useState(false)
  const location = useLocation(), navigate = useNavigate()
  const navRef=useRef(null),menuRef=useRef(null)
  const [mobile,setMobile]=useState(()=>typeof window!=='undefined'&&window.matchMedia('(max-width: 860px)').matches)
  useEffect(()=>{const media=window.matchMedia('(max-width: 860px)');const update=()=>{setMobile(media.matches);if(!media.matches)setNavOpen(false)};media.addEventListener('change',update);return()=>media.removeEventListener('change',update)},[])
  useEffect(()=>{
    if(!mobile||!navOpen)return
    const panel=navRef.current,trigger=menuRef.current
    const controls=()=>[...panel.querySelectorAll('a[href],button:not(:disabled),summary,[tabindex="0"]')].filter(el=>el.getClientRects().length>0)
    controls()[0]?.focus()
    const keydown=event=>{
      if(event.key==='Escape'){event.preventDefault();setNavOpen(false);return}
      if(event.key!=='Tab')return
      const items=controls(),first=items[0],last=items.at(-1)
      if(!first){event.preventDefault();return}
      if(event.shiftKey&&(document.activeElement===first||!panel.contains(document.activeElement))){event.preventDefault();last.focus()}
      else if(!event.shiftKey&&(document.activeElement===last||!panel.contains(document.activeElement))){event.preventDefault();first.focus()}
    }
    document.addEventListener('keydown',keydown)
    return()=>{document.removeEventListener('keydown',keydown);trigger?.focus()}
  },[mobile,navOpen])

  // Close the mobile drawer whenever the route changes.
  useEffect(() => {
    setNavOpen(false)
  }, [location.pathname,location.search])

  return (
    <div className={styles.shell}>
      <Sidebar open={navOpen} mobile={mobile} panelRef={navRef} onClose={() => setNavOpen(false)} />
      <div
        className={styles.scrim}
        data-open={navOpen}
        onClick={() => setNavOpen(false)}
        aria-hidden="true"
      />
      <div className={styles.main} inert={mobile&&navOpen?'':undefined}>
        {/* Above the top bar and on every route — impersonation is never
            something you can scroll away from. */}
        <ActingAsBanner />
        <TopBar menuRef={menuRef} navOpen={mobile&&navOpen} onMenu={() => setNavOpen((v) => !v)} />
        <main className={styles.content}>
          <ErrorBoundary key={location.pathname} label="This page" onReset={()=>navigate('/',{replace:true})}><PlatformRouteGate><Outlet /></PlatformRouteGate></ErrorBoundary>
        </main>
        <Footer />
      </div>
    </div>
  )
}
